import { useEffect, useSyncExternalStore } from 'react';
import { useSearchParams } from 'react-router-dom';
import { z, ZodError } from 'zod';

import { useLogEvent } from '@autopass-web/mochi-analytics/event';

import { submitInfoStorage } from './use-submit-records';

import type { PaymentMethodType } from '@autopass-web/types';

// Here is the valid error retryReason which registered. the format is {payment_method}:{reason}
const retryErrorSchema = z.enum([
  'autopass:app_not_found',
  'autopass:payment_flow_incomplete',
  'auto_partner:payment_flow_incomplete',
]);

const RecordSchema = z.object({
  paymentMethod: z.string().nullable(),
  reason: z.string().optional(),
});

const SubmitInfoSchema = z.object({
  invoiceId: z.string(),
  records: z.array(RecordSchema),
});

type Record = {
  paymentMethod: null | string;
  reason?: string;
};
type SubmitInfo = z.infer<typeof SubmitInfoSchema>;
type InvoiceId = SubmitInfo['invoiceId'];
/**
 * 1. 改成 append only
 * 2. cleanup 機制要改
 * 3. 乾脆用新的 key 好了 zzz
 * 4. 大假設：一個時間只會進行一個連逤電較一
 */

/** store the submit error and payment type for persist client side storage, which implement with LocalStorage. */
class SubmitInfoStorageV2 {
  #KEY = '#submit_info_v2';
  #storage: Storage | undefined;
  #listener?: () => void | undefined;
  #added: boolean;

  constructor() {
    try {
      // test is localstorage working or not, if not. use the polyfill memory storage (just pure object in closure)
      const TEST_KEY = '__TEST__';
      window.localStorage.setItem(TEST_KEY, '');
      window.localStorage.removeItem(TEST_KEY);

      this.#storage = window.localStorage;
    } catch {
      this.#storage = this.#createStoragePolyfill();
    }

    submitInfoStorage.delete();
    this.#added = false;
  }

  #createStoragePolyfill = () => {
    const obj = {};
    return {
      getItem: (key) => obj?.[key],
      setItem: (key, item) => {
        obj[key] = item;
      },
    } as Storage;
  };

  #createEmptySubmitInfo = (invoiceId: InvoiceId): SubmitInfo => ({
    invoiceId,
    records: [],
  });

  #getInfo = (invoiceId: InvoiceId) => {
    try {
      if (!this.#storage) throw new Error();
      const submitInfoText = this.#storage.getItem(this.#KEY);
      // parse the data from storage every time, make sure all data is schema matched
      const submitInfo = JSON.parse(submitInfoText);

      const parsedSubmitInfo = SubmitInfoSchema.parse(submitInfo);

      return parsedSubmitInfo;
    } catch {
      console.trace();
      return this.#createEmptySubmitInfo(invoiceId);
    }
  };

  #setInfo = (info: SubmitInfo) => {
    try {
      SubmitInfoSchema.parse(info);

      const infoString = JSON.stringify(info);

      this.#storage.setItem(this.#KEY, infoString);

      // trigger React rerender
      this.#listener?.();
    } catch (err) {
      console.trace();
      if (err instanceof ZodError) {
        console.error(err.message, info);
      } else {
        console.warn(err.message);
      }
    }
  };

  addRecord = (invoiceId: InvoiceId, record?: Record) => {
    if (this.#added) {
      return;
    }
    const info = this.#getInfo(invoiceId);

    const storageRecord: Record = record ?? {
      paymentMethod: null,
    };

    info.records.push(storageRecord);

    this.#setInfo(info);
    this.#added = true;

    return info;
  };

  getRecords = (invoiceId: InvoiceId) => {
    const info = this.#getInfo(invoiceId);

    return info.records;
  };

  subscribe = (cb: () => void) => {
    this.#listener = cb;

    this.#listener();
    return () => {
      this.#listener = undefined;
    };
  };

  getSnapshot = () => {
    return this;
  };

  cleanup = () => {
    this.#storage.removeItem(this.#KEY);
  };
}

const submitInfoStorageV2 = new SubmitInfoStorageV2();

const useSubmitRecordsV2 = (invoiceId: InvoiceId) => {
  const KEY = 'retry_reason';
  const [searchParams, setSearchParams] = useSearchParams();
  const submitInfo = useSyncExternalStore(
    submitInfoStorageV2.subscribe,
    submitInfoStorageV2.getSnapshot,
  );

  const log = useLogEvent('telemetry', '付款確認資訊');

  useEffect(() => {
    const init = () => {
      // put the error record into local storage which from search
      const retryReasonParam = searchParams.get(KEY);

      // validate the error code
      const result = retryErrorSchema.safeParse(retryReasonParam);

      if (!result.success) {
        return submitInfoStorageV2.addRecord(invoiceId);
      }

      const validRetryReason = result.data;

      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const [_match, paymentMethod, reason] =
        validRetryReason.match(/(\w+):(\w+)/);

      const info = submitInfoStorageV2.addRecord(invoiceId, {
        paymentMethod,
        reason,
      });

      log({ records: JSON.stringify(info.records), invoiceId });

      searchParams.delete(KEY);

      // clean up queryStrings to prevent reload adding new records
      setSearchParams(searchParams.toString(), { replace: true });
    };

    init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invoiceId]);

  const records = submitInfo.getRecords(invoiceId);

  return {
    records,
    addRecords: (paymentMethod: PaymentMethodType) =>
      submitInfo.addRecord(invoiceId, { paymentMethod }),
    cleanup: () => submitInfo.cleanup(),
  };
};

export { useSubmitRecordsV2 };
